/**
 * Habit Tracker — Daily habits with streaks and heatmaps.
 */

import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import {
  Plus, Trash2, Flame, CheckSquare, Target,
} from "lucide-react";

interface Habit {
  id: number;
  name: string;
  slug: string;
  icon: string;
  color: string;
  target_per_day: number;
  unit: string;
}

interface HabitStreak {
  id: number;
  name: string;
  icon: string;
  color: string;
  target_per_day: number;
  unit: string;
  streak: number;
  longest_streak: number;
  total_entries: number;
  heatmap: { date: string; value: number }[];
}

const today = () => new Date().toISOString().split("T")[0]!;

export function HabitsRoute() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: "", color: "#00f0ff", target_per_day: 1, unit: "times" });

  // Queries
  const { data: habits } = useQuery<Habit[]>({
    queryKey: ["habits"],
    queryFn: () => fetch("/api/habits").then((r) => r.json()),
  });

  const { data: streakData } = useQuery<{ habits: HabitStreak[] }>({
    queryKey: ["habits", "streaks"],
    queryFn: () => fetch("/api/habits/streaks").then((r) => r.json()),
  });

  const streaks = streakData?.habits ?? [];

  // Mutations
  const createHabit = useMutation({
    mutationFn: (data: typeof newHabit) =>
      fetch("/api/habits", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }).then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
      setShowCreate(false);
      setNewHabit({ name: "", color: "#00f0ff", target_per_day: 1, unit: "times" });
    },
  });

  const deleteHabit = useMutation({
    mutationFn: (id: number) => fetch(`/api/habits/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["habits"] }),
  });

  const logEntry = useMutation({
    mutationFn: (data: { habit_id: number; date: string; value: number }) =>
      fetch("/api/habits/entries", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }).then((r) => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["habits"] }),
  });

  const handleToggleToday = useCallback((habitId: number, currentValue: number, target: number) => {
    logEntry.mutate({
      habit_id: habitId,
      date: today(),
      value: currentValue >= target ? 0 : target,
    });
  }, [logEntry]);

  const inputClass = "bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-sm font-mono px-3 py-2 w-full focus:border-[var(--bw-accent)] focus:outline-none";

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <CRTHeader text="Habits" level={2} />
        <GlyphButton variant="secondary" size="sm" icon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
          Nuevo
        </GlyphButton>
      </div>

      {/* Create form */}
      {showCreate && (
        <CorpoCard title="NUEVO HÁBITO" accent>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Nombre</label>
              <input value={newHabit.name} onChange={(e) => setNewHabit((h) => ({ ...h, name: e.target.value }))} placeholder="Ej: Agua 2L" className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Color</label>
              <input type="color" value={newHabit.color} onChange={(e) => setNewHabit((h) => ({ ...h, color: e.target.value }))} className="w-full h-9 bg-transparent border border-[var(--bw-border)] cursor-pointer" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Meta / día</label>
              <input type="number" min={1} value={newHabit.target_per_day} onChange={(e) => setNewHabit((h) => ({ ...h, target_per_day: parseInt(e.target.value) || 1 }))} className={inputClass} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <GlyphButton size="sm" onClick={() => createHabit.mutate(newHabit)} disabled={!newHabit.name.trim()}>Crear</GlyphButton>
            <GlyphButton variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancelar</GlyphButton>
          </div>
        </CorpoCard>
      )}

      {/* Habits grid with streaks + heatmaps */}
      {streaks.length > 0 ? (
        <div className="space-y-4">
          {streaks.map((h) => {
            const todayValue = h.heatmap[h.heatmap.length - 1]?.value ?? 0;
            const doneToday = todayValue >= h.target_per_day;

            return (
              <CorpoCard key={h.id}>
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => handleToggleToday(h.id, todayValue, h.target_per_day)}
                    className="w-8 h-8 border-2 grid place-items-center transition-all"
                    style={{
                      borderColor: h.color,
                      backgroundColor: doneToday ? h.color : "transparent",
                      boxShadow: doneToday ? `0 0 10px ${h.color}` : "none",
                    }}
                  >
                    {doneToday && <CheckSquare size={16} className="text-[var(--bw-bg)]" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[var(--bw-fg)]">{h.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Flame size={12} style={{ color: h.color }} />
                      <span className="text-xs" style={{ color: h.color }}>{h.streak} días</span>
                      <span className="text-[10px] text-[var(--bw-muted)]">
                        (max: {h.longest_streak})
                      </span>
                      <HexBadge variant="muted" size="sm">{h.total_entries} logs</HexBadge>
                    </div>
                  </div>

                  <button
                    onClick={() => deleteHabit.mutate(h.id)}
                    className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Heatmap */}
                <div className="flex flex-wrap gap-[2px]">
                  {h.heatmap.map((day) => (
                    <div
                      key={day.date}
                      className="w-[11px] h-[11px] border border-[var(--bw-border)]"
                      style={{
                        backgroundColor: day.value >= h.target_per_day
                          ? h.color
                          : day.value > 0
                            ? `${h.color}40`
                            : "var(--bw-surface-2)",
                        boxShadow: day.value >= h.target_per_day ? `0 0 3px ${h.color}` : "none",
                      }}
                      title={`${day.date}: ${day.value}`}
                    />
                  ))}
                </div>
              </CorpoCard>
            );
          })}
        </div>
      ) : (
        <CorpoCard>
          <div className="text-center py-8">
            <Target size={48} className="mx-auto text-[var(--bw-muted)] mb-3" />
            <p className="text-sm text-[var(--bw-muted)]">Sin hábitos configurados</p>
            <p className="text-xs text-[var(--bw-muted)] mt-1">Crea un hábito para empezar a trackear</p>
          </div>
        </CorpoCard>
      )}
    </div>
  );
}
